import axios from 'axios';

// ─── Public Tracking API ──────────────────────────────────────────────
// GET /v1/public/track/{waybill} — the ONE unauthenticated endpoint.
//
// This uses its OWN bare axios instance, deliberately NOT the shared
// '@/utils/axios' client. The shared client (a) attaches the bearer token on
// every request and (b) force-signs-out on any 401. A public tracking page must
// work with no session, must never send an Authorization header, and must never
// trigger a sign-out because of an unrelated failure. So it gets a clean client
// with no interceptors.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const publicClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'dash-client-type': 'web',
  },
});

export interface PublicTrackParty {
  name: string;
  /** Coarse (district/city) — server-masked. `null` renders as "—". */
  address: string | null;
  /** Derived kode pos, or `null` when not confidently found. */
  postalCode: string | null;
  /** Server-masked, e.g. `08••••7890`, or `null`. */
  phone: string | null;
}

export interface PublicTrackTariff {
  serviceName: string;
  amount: number;
  currency: string;
}

/** "Informasi mengenai Kiriman" — gov-required shipment info block. */
export interface PublicTrackShipmentInfo {
  itemCount: number;
  totalKoli: number;
  weightKg: number;
}

/** One status-history entry, newest-first. */
export interface PublicTrackTimelineEvent {
  status: string;
  /** Coarse city for the event; null when not derivable. */
  location: string | null;
  occurredAt: string;
}

export interface PublicTrack {
  waybill: string;
  status: string;
  sender: PublicTrackParty;
  receiver: PublicTrackParty;
  shipment: PublicTrackShipmentInfo;
  /** Hub facility name (shown at the "Di Hub" step); null when unset. */
  hubName: string | null;
  /** Generic courier label ("Kurir Dash") once dispatched; null before. */
  courier: string | null;
  timeline: PublicTrackTimelineEvent[];
  tariff: PublicTrackTariff;
  createdAt: string;
  updatedAt: string;
}

interface PublicTrackEnvelope {
  status: 'Success' | 'Failed';
  data?: PublicTrack;
}

/** Thrown when the waybill is unknown (HTTP 404). Lets the page show a clean
 *  "not found" state distinct from a network/server error. */
export class PublicTrackNotFoundError extends Error {
  constructor(message = 'Shipment not found') {
    super(message);
    this.name = 'PublicTrackNotFoundError';
  }
}

/** HTTP 429 — the public endpoint is rate-limited; the caller shows a
 *  "try again shortly" state distinct from a generic connection error. */
export class PublicTrackRateLimitedError extends Error {
  constructor(message = 'Too many requests') {
    super(message);
    this.name = 'PublicTrackRateLimitedError';
  }
}

export const getPublicTrack = async (waybill: string): Promise<PublicTrack> => {
  try {
    const res = await publicClient.get<PublicTrackEnvelope>(
      `/v1/public/track/${encodeURIComponent(waybill)}`
    );
    if (!res.data?.data) {
      throw new Error('Malformed public track response');
    }
    return res.data.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 404) throw new PublicTrackNotFoundError();
      if (err.response?.status === 429) throw new PublicTrackRateLimitedError();
    }
    throw err;
  }
};
